import { CompareArrowsOutlined, FileUploadOutlined } from "@mui/icons-material";
import { Box, Button, Divider } from "@mui/material";
import { grey } from "@mui/material/colors";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import PropTypes from "prop-types";
import { Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import PrimaryContentCard from "../../../../_src_shared/components/layout/PrimaryContentCard";
import Flx from "../../../../components/layout/Flx";
import LoadingFallback from "../../../../components/LoadingFallback";
import Txt from "../../../../components/typography/Txt";
import { loanDrilldownSidebarTypes } from "../../../../constants/enums/loanDrilldownScreenSidebarOptions";
import { useLoanFilesHook } from "../../../../hooks/useLoanFilesHook";
import { sidebarTypeToggle } from "../../../../redux/actions/sidebarActions";
import { loanFetchedDataSizerUploadsSet } from "../../../../redux/reducers/loanFetchedDataReducer";
import LoanDrilldownTabPanel from "../LoanDrilldownTabPanel";
import LoanSizerUploadsTable from "./LoanSizerUploadsTable";
import SizerCurrentCard from "./SizerCurrentCard";
import SizerNeedsClassificationSection, {
  getUnclassifiedSizerDocs,
} from "./SizerNeedsClassificationSection";
import { sortByUploadDateDesc } from "./sizerDisplay";

// Uppercase micro-label separating the sub-sections inside the single card.
const SectionLabel = ({ children, endContent }) => (
  <Flx jb ac sx={{ mb: 1.5 }}>
    <Txt
      sx={{
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: "0.05em",
        textTransform: "uppercase",
        color: "#64748b",
      }}
    >
      {children}
    </Txt>
    {endContent}
  </Flx>
);

const LoanDrilldownSizerTab = ({ value, tab }) => {
  const dispatch = useDispatch();
  const loanDrilldown = useSelector((state) => state?.loanDrilldown);
  const sizerUploads = useSelector(
    (state) => state?.loanFetchedData?.sizerUploads,
  );
  const { getAllSizerUploads, getAllSizerUploadsByDealId } = useLoanFilesHook();
  const [compareMode, setCompareMode] = useState(false);

  const fetchSizers = useCallback(() => {
    if (isNil(loanDrilldown?._id)) return;
    getAllSizerUploadsByDealId({
      dealId: loanDrilldown?._id,
      onSuccessFn: (data) =>
        dispatch(loanFetchedDataSizerUploadsSet(data || [])),
      onFailFn: () => {
        getAllSizerUploads({
          onSuccessFn: (data) =>
            dispatch(loanFetchedDataSizerUploadsSet(data || [])),
          onFailFn: (error) => {
            console.error("Error fetching sizer uploads:", error);
          },
        });
      },
    });
  }, [
    loanDrilldown?._id,
    dispatch,
    getAllSizerUploadsByDealId,
    getAllSizerUploads,
  ]);

  useEffect(() => {
    if (isNil(sizerUploads)) {
      fetchSizers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Newest-first: index 0 is the "current" sizer that drives loan terms.
  const orderedRows = useMemo(() => {
    const forDeal = sizerUploads?.filter(
      (row) => row?.dealId === loanDrilldown?._id,
    );
    return sortByUploadDateDesc(forDeal);
  }, [sizerUploads, loanDrilldown?._id]);

  // Map each row to the next-older row so the table/hero can show Δ-vs-prior.
  const priorById = useMemo(() => {
    const map = {};
    orderedRows.forEach((row, i) => {
      if (row?._id) map[row._id] = orderedRows[i + 1] || null;
    });
    return map;
  }, [orderedRows]);

  const hasSizers = !isEmpty(orderedRows);
  const current = orderedRows[0];
  const prior = orderedRows[1];

  const candidates = useMemo(
    () => getUnclassifiedSizerDocs(loanDrilldown?.loanDocuments, sizerUploads),
    [loanDrilldown?.loanDocuments, sizerUploads],
  );
  const hasCandidates = candidates.length > 0;

  const onUploadSizerClick = useCallback(() => {
    dispatch(sidebarTypeToggle(loanDrilldownSidebarTypes.SIZER_UPLOAD));
  }, [dispatch]);

  return (
    <LoanDrilldownTabPanel value={value} tab={tab}>
      <PrimaryContentCard
        maxWidth="xl"
        loading={isNil(sizerUploads)}
        title="Sizers"
        subTitle="Pricing worksheets uploaded to this loan. The current sizer drives loan terms."
        endContent={
          <Flx ac g={1}>
            {orderedRows.length > 1 && (
              <Button
                size="medium"
                variant={compareMode ? "contained" : "outlined"}
                startIcon={<CompareArrowsOutlined />}
                onClick={() => setCompareMode((v) => !v)}
              >
                Compare versions
              </Button>
            )}
            <Button
              size="medium"
              variant="contained"
              startIcon={<FileUploadOutlined />}
              onClick={onUploadSizerClick}
            >
              Upload sizer
            </Button>
          </Flx>
        }
      >
        <Divider sx={{ mt: 0.5, mb: 3 }} />

        {hasCandidates && (
          <Box sx={{ mb: hasSizers ? 4 : 0 }}>
            <SizerNeedsClassificationSection candidates={candidates} />
          </Box>
        )}

        {hasSizers ? (
          <Flx column g={3}>
            <SizerCurrentCard
              current={current}
              prior={prior}
              total={orderedRows.length}
              onUpload={onUploadSizerClick}
            />

            {orderedRows.length > 1 && (
              <Box>
                <SectionLabel>
                  Version history ({orderedRows.length} uploads)
                </SectionLabel>
                <Box
                  sx={{
                    border: `1px solid ${grey[200]}`,
                    borderRadius: "12px",
                    overflow: "hidden",
                  }}
                >
                  <Suspense fallback={<LoadingFallback />}>
                    <LoanSizerUploadsTable
                      rowData={orderedRows}
                      currentId={current?._id}
                      priorById={priorById}
                      compareMode={compareMode}
                    />
                  </Suspense>
                </Box>
              </Box>
            )}
          </Flx>
        ) : (
          !hasCandidates && (
            <Flx column ac g={1.5} sx={{ py: 6 }}>
              <Txt secondary sx={{ fontStyle: "italic" }}>
                No sizers on this loan yet.
              </Txt>
              <Button
                variant="contained"
                startIcon={<FileUploadOutlined className="thin" />}
                onClick={onUploadSizerClick}
              >
                Upload sizer
              </Button>
            </Flx>
          )
        )}
      </PrimaryContentCard>
    </LoanDrilldownTabPanel>
  );
};

LoanDrilldownSizerTab.propTypes = {
  value: PropTypes.any,
  tab: PropTypes.any,
};

export default LoanDrilldownSizerTab;
